import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { getFullLocaleFromConfig, environment, customArrayMerge } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';
import { orderConfig } from './order/order';

const tenantId = 'jelmolich';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'jelmoli-shop.ch', appDeeplinkProtocol: 'jchapp' },
    clientId: 'JelmoliCh',
    i18n: {
      defaultLocale: 'de-CH',
      locales: ['de-CH', 'fr-CH'],
      languages: ['de', 'fr'],
    },
    tenantId,
    styleVariant: 'dark',
    company: {
      name: 'Jelmoli',
      seoTitleName: 'jelmoli-shop.ch',
    },
    timeZone: 'Europe/Zurich',
    headerSlots: {
      localeSwitcher: true,
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: `https://storage.googleapis.com/drupal-images-${tenantId}/$1/`,
      },
      assetServerHost: 'https://bilder.jelmoli-shop.ch',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `http://cloud-service-cms.${tenantId}.svc.cluster.local:3100`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    search: {
      adServer: {
        mode: 'containerOnly',
      },
    },
    middleware: {
      cmsRewrites: {
        exact: ['/', '/storefront', '/fr', '/mafo/unsubscribe', '/uwg7/unsubscribe'],
        prefixes: [
          '/service-hilfe',
          '/aide-service',
          '/lp',
          '/content',
          '/magazin',
          '/magazine',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    order: {
      supportedPaths: {
        myAccountAddresses: true,
      },
      seals: {},
      assets: {
        premium: {
          getPremiumImage: ({ language, device }: { language: string; device: string }) =>
            `https://bilder.jelmoli-shop.ch/empiriecom/io/jelmolich/${language.split('-')[0]}/BS3_CheckOut_${device}.jpg`,
        },
        gifs: {
          success: {
            url: 'https://bilder.jelmoli-shop.ch/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      accountInformation: {
        bicAvailable: false,
      },
      addressValidation: {
        zipCode: {
          length: 4,
        },
      },
      bankInformation: {
        companyName: 'Jelmoli Versand AG',
        bankName: 'Industriestrasse 19 8112 Otelfingen',
        iban: 'CH25 3000 0001 7022 8143 3',
        bic: 'DRESDEFF760',
      },
      ...orderConfig,
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      newsletterSettings: {
        useDOI: false,
      },
      contact: {
        dynamicHelp,
      },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|99'],
        fields: [
          {
            id: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|99',
            options: [
              {
                id: 'frq-daily',
                value: 'frequency|0',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.daily',
              },
              {
                id: 'frq-weekly',
                value: 'frequency|1',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.weekly',
              },
              {
                id: 'unsubscribe',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink:
            'https://www.jelmoli-shop.ch/service-hilfe/vorteile/newsletter-abmelden/',
          voucherLink: 'https://www.jelmoli-shop.ch/service-hilfe/vorteile/gutscheine-rabatte/',
        },
        fr: {
          unsubscribeLink:
            'https://www.jelmoli-shop.ch/fr/aide-service/avantages/desinscription-de-la-newsletter',
          voucherLink: 'https://www.jelmoli-shop.ch/fr/aide-service/avantages/bons-rabais/',
        },
      },
      braFinder: {
        de: { searchLink: '/s/CUP+{cupSize}%2C+Grösse+{size}' },
      },
      validation: {
        rules: {
          fileUpload: {
            maxFileSizeMb: 20,
            maxFileSizeSummaryMb: 20,
            acceptedFormats: 'image/*,.heic,.heif,.mov',
            regexAcceptedUploadFormats: /\.(jpe?g|png|gif|webp|bmp|heic|heif|mov)$/i,
          },
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
      url: {
        'de-CH': {
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialActionTarget: 'https://www.jelmoli-shop.ch/s/{search_term}/',
        },
        'fr-CH': {
          privacyNoticePath: '/fr/aide-service/qui-sommes-nous/protection-des-donnees',
          potentialActionTarget: 'https://www.jelmoli-shop.ch/s/{search_term}/',
        },
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: { appUrl: 'https://app.adjust.com/908rzj9_i331frl' },
    tracking: {
      categoryIdHomepage: '124601',
      soastaId: 'E5TKR-EUQHN-KSJKK-TGX8P-4US3H',
      tagmanagerId: 'GTM-5XXTXXK',
      googleSiteVerificationID: ['fZ1lyGUwe-g4AMGjHievvd2bqXuyPGQGB-lX4odp2pM'],
      msvalidateID: '4067315BE5669CD6C74BDEC6F6AC5D47',
      pinterestRichPinVerificationID: '8f3ef5e6b998a08858555b19af641e9c',
      schemaOrgOrganisation: {
        'de-CH': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.jelmoli-shop.ch/',
          name: 'Jelmoli Versand',
          alternateName: 'Jelmoli',
          description:
            'Jelmoli Versand ist ein Schweizer Online-Shop, der ein breites Sortiment an hochwertigen Produkten anbietet, darunter Mode, Beauty, Schuhe, Sportartikel und Wohnaccessoires. Jelmoli ist bekannt für seine sorgfältige Produktauswahl und sein Engagement für Qualität und Kundenservice. Jelmoli ist *nicht* nur ein einfacher Online-Händler, sondern ein Anbieter für ein Einkaufserlebnis mit Fokus auf Qualität und Lifestyle.',
          url: 'https://www.jelmoli-shop.ch/',
          logo: 'https://www.jelmoli-shop.ch/assets/logos/jelmolich/logo.svg',
          foundingDate: '1849-01-01',
          founder: {
            '@type': 'Person',
            name: 'Johann Peter Jelmoli',
            description: 'Gründer von Jelmoli',
            sameAs: 'https://de.wikipedia.org/wiki/Johann_Peter_Jelmoli',
          },
          location: {
            '@type': 'Place',
            name: 'Otelfingen',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Industriestrasse 19',
              addressLocality: 'Otelfingen',
              addressRegion: 'ZH',
              postalCode: '8112',
              addressCountry: 'CH',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+41848-840-300',
              email: 'service@jelmoliversand.ch',
              availableLanguage: ['de', 'fr'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.jelmoli-shop.ch/service-hilfe/ueber-uns/kontakt',
            },
          ],
          sameAs: [
            'https://www.facebook.com/jelmoliversand/',
            'https://www.youtube.com/channel/UCJunBjLDyBOG2mQD-K1bkhQ',
            'https://www.instagram.com/jelmoliversand/',
            'https://de.wikipedia.org/wiki/Jelmoli',
          ],
          legalName: 'Jelmoli Versand AG',
          areaServed: 'CH',
          memberOf: { '@type': 'Organization', name: 'Otto Group' },
          foundingLocation: 'Valle Vigezzo',
          slogan: 'Jelmoli, das pure Vergnügen, Neues zu entdecken.',
        },
        'fr-CH': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.jelmoli-shop.ch/fr/',
          name: 'Jelmoli Versand',
          alternateName: 'Jelmoli',
          description:
            "Jelmoli Versand est une boutique en ligne suisse proposant un large éventail de produits de haute qualité, notamment de la mode, des produits de beauté, des chaussures, des articles de sport et des accessoires pour la maison. Jelmoli est connu pour sa sélection rigoureuse de produits et son engagement envers la qualité et le service clientèle. Jelmoli n'est *pas* seulement un simple détaillant en ligne, mais un fournisseur d'une expérience d'achat axée sur la qualité et le style de vie.",
          url: 'https://www.jelmoli-shop.ch/fr/',
          logo: 'https://www.jelmoli-shop.ch/assets/logos/jelmolich/logo.svg',
          foundingDate: '1849-01-01',
          founder: {
            '@type': 'Person',
            name: 'Johann Peter Jelmoli',
            description: 'Fondateur de Jelmoli',
            sameAs: 'https://en.wikipedia.org/wiki/Johann_Peter_Jelmoli',
          },
          location: {
            '@type': 'Place',
            name: 'Otelfingen',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Industriestrasse 19',
              addressLocality: 'Otelfingen',
              addressRegion: 'ZH',
              postalCode: '8112',
              addressCountry: 'CH',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+41848-840-300',
              email: 'service@jelmoliversand.ch',
              availableLanguage: ['de', 'fr'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.jelmoli-shop.ch/fr/aide-service/qui-sommes-nous/contactez-nous',
            },
          ],
          sameAs: [
            'https://www.facebook.com/jelmoliversand/',
            'https://www.youtube.com/channel/UCJunBjLDyBOG2mQD-K1bkhQ',
            'https://www.instagram.com/jelmoliversand/',
            'https://fr.wikipedia.org/wiki/Jelmoli',
          ],
          legalName: 'Jelmoli Versand AG',
          areaServed: 'CH',
          memberOf: { '@type': 'Organization', name: 'Otto Group' },
          foundingLocation: 'Valle Vigezzo',
          slogan: 'Jelmoli, le pur plaisir de découvrir de nouvelles choses.',
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'Entdecken Sie aktuelle Fashion- und Wohntrends. Grosse Auswahl ✔ Top-Brands ✔ Gratis Versand &amp; Retoure ✔ Machen Sie sich eine Freude!',
        title: 'Jelmoli Versand - der online Mode und M&ouml;bel Versand',
        canonical: 'https://www.jelmoli-shop.ch',
        robots: 'index,follow',
      },
      fr: {
        description:
          "Description:* Mode et meubles à commander SUR FACTURE - robes, chaussures, lingerie, maillots, sport, meubles et articles techniques. L'embarras du choix sur JELMOLI VERSAND SUISSE",
        title: 'Jelmoli Versand Suisse - vente de mode et meubles à distance',
        canonical: 'https://www.jelmoli-shop.ch',
        robots: 'index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      tenantId: '1300',
    },
    dynamicYieldTracking: {
      key: '85fed729032b18a2a240c7c5fd478e30951dd4885b6ae83fbd5e5e6b0b633914',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'ch',
      alternatives: [
        {
          flag: 'de',
          shops: [{ link: 'https://www.baur.de' }],
        },
        {
          flag: 'at',
          shops: [{ link: 'https://www.ottoversand.at' }],
        },
      ],
    },
    priceFormat: 'NoDelimiterCurrencyAtTheEnd',
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
